// src/pages/SearchLeadsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Card,
  Typography,
  InputAdornment,
  Pagination,
  Checkbox,
  Chip,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../utils/api";
import { toast } from "react-toastify";
import LeadStatusCell from "../components/LeadStatusCell";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import { LEAD_STATUS_DEFINITIONS } from "../constants/leadStatuses";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import AddSearchLeadDialog from "../components/AddSearchLeadDialog";
import { US_STATES, formatStateAbbr, normalizeStateValue } from "../constants/usStates";
import { displayLeadCategoryLabel } from "../utils/leadCategoryLabel";
import {
  getLeadCategoryOutlineColor,
  leadCategoryChipSx,
} from "../utils/leadCategoryChip";
import { formatPhoneDisplay } from "../utils/formatPhone";
import { Plus, Search } from "lucide-react";
import "./CustomersPage.css";
import "./SearchLeadsPage.css";

const SEARCH_LEADS_STATE_KEY = "searchLeadsState";
const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];

/** Outlined Previous/Next: primary fill + white text on hover */
const paginationNavButtonHoverSx = {
  "&:hover:not(.Mui-disabled)": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderColor: "primary.main",
  },
};

const defaultFilters = {
  status: "all",
  category: "all",
  territory: "all",
  assigned_rep: "all",
  property_type: "all",
  start_date: "",
  end_date: "",
  archived: "false",
  proposal_status: "all",
};

const defaultRefine = {
  first_name: "",
  last_name: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
};

function LeadFilterRow({ label, children }) {
  return (
    <label className="customers-filter-row">
      <span className="customers-filter-label">{label}</span>
      <span className="customers-filter-control">{children}</span>
    </label>
  );
}

function sanitizeRefine(raw) {
  if (!raw || typeof raw !== "object") return { ...defaultRefine };
  return {
    first_name: typeof raw.first_name === "string" ? raw.first_name : "",
    last_name: typeof raw.last_name === "string" ? raw.last_name : "",
    address: typeof raw.address === "string" ? raw.address : "",
    city: typeof raw.city === "string" ? raw.city : "",
    state: typeof raw.state === "string" ? normalizeStateValue(raw.state) : "",
    zip_code: typeof raw.zip_code === "string" ? raw.zip_code : "",
    phone: typeof raw.phone === "string" ? raw.phone : "",
  };
}

function countFilledRefine(refine) {
  return Object.values(refine || {}).filter((v) => String(v || "").trim()).length;
}

function getInitialSearchState() {
  try {
    const raw = sessionStorage.getItem(SEARCH_LEADS_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      searchText: typeof parsed.searchText === "string" ? parsed.searchText : "",
      filters: { ...defaultFilters, ...(parsed.filters && typeof parsed.filters === "object" ? parsed.filters : {}) },
      refine: sanitizeRefine(parsed.refine),
      page: typeof parsed.page === "number" && parsed.page >= 0 ? parsed.page : 0,
      rowsPerPage: typeof parsed.rowsPerPage === "number" && parsed.rowsPerPage > 0 ? parsed.rowsPerPage : 15,
    };
  } catch {
    return null;
  }
}

export default function SearchLeadsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { role, isReadOnly, user, can } = useAuth();
  const canViewCategory = role !== "Sales Rep";
  const canDeleteLeads =
    (role === "Admin" || role === "Sales Director") && !isReadOnly;

  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [leadCategories, setLeadCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search and filter state (initialized from sessionStorage when returning from lead detail)
  const [searchText, setSearchText] = useState(() => {
    const s = getInitialSearchState();
    return s?.searchText ?? "";
  });
  const [filters, setFilters] = useState(() => {
    const s = getInitialSearchState();
    const initial = s?.filters ? { ...defaultFilters, ...s.filters } : { ...defaultFilters };
    if (role === "Sales Rep" && initial.status === "Closed Won" && user?.id != null) {
      initial.assigned_rep = user.id;
    }
    return initial;
  });
  const lockAssignedRepToSelf = role === "Sales Rep" && filters.status === "Closed Won";
  const [refine, setRefine] = useState(() => {
    const s = getInitialSearchState();
    return s?.refine ?? { ...defaultRefine };
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showRefine, setShowRefine] = useState(() => {
    const s = getInitialSearchState();
    return countFilledRefine(s?.refine) > 0;
  });
  const [page, setPage] = useState(() => {
    const s = getInitialSearchState();
    return s?.page ?? 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = getInitialSearchState();
    return s?.rowsPerPage ?? 15;
  });
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const querySignature = useMemo(
    () => JSON.stringify({ searchText, filters, refine }),
    [searchText, filters, refine]
  );
  const prevQuerySigRef = useRef(null);

  // Mobile card styling (same pattern as TerritoriesPage)
  const mobileLeadCardSx = {
    p: 2.25,
    borderRadius: 3,
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(30,41,59,0.88)"
        : "background.paper",
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0,0,0,0.55)"
        : "0 4px 12px rgba(0,0,0,0.12)",
    cursor: "pointer",
    "&:active": {
      transform: "scale(0.995)",
    },
  };



  // Persist search and filter state to sessionStorage (restored when returning from lead detail)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        SEARCH_LEADS_STATE_KEY,
        JSON.stringify({ searchText, filters, refine, page, rowsPerPage })
      );
    } catch {
      // ignore quota or parse errors
    }
  }, [searchText, filters, refine, page, rowsPerPage]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [territoriesRes, usersRes] = await Promise.all([
          API.get("/territories/"),
          API.get("/users/"),
        ]);
        setTerritories(territoriesRes.data);
        setUsers(usersRes.data);
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
        toast.error("Failed to load filter options");
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!canViewCategory) return;
      try {
        const res = await API.get("/lead-categories/");
        const rows = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setLeadCategories(rows);
      } catch (err) {
        console.error("Error fetching lead categories:", err);
      }
    };
    fetchCategories();
  }, [canViewCategory]);

  // Fetch leads with search and filters
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const filledRefine = Object.fromEntries(
        Object.entries(refine).filter(([, v]) => String(v || "").trim())
      );
      const refineActive = Object.keys(filledRefine).length > 0;

      if (refineActive) {
        Object.entries(filledRefine).forEach(([key, value]) => {
          params.append(key, String(value).trim());
        });
      } else if (searchText.trim()) {
        params.append("search", searchText.trim());
      }

      if (filters.status && filters.status !== "all") {
        params.append("status", filters.status);
      }
      if (filters.category && filters.category !== "all") {
        params.append("category", filters.category);
      }
      if (filters.territory && filters.territory !== "all") {
        params.append("territory", filters.territory);
      }
      if (filters.assigned_rep && filters.assigned_rep !== "all") {
        params.append("assigned_rep", filters.assigned_rep);
      }
      if (filters.property_type && filters.property_type !== "all") {
        params.append("property_type", filters.property_type);
      }
      if (filters.proposal_status && filters.proposal_status !== "all") {
        params.append("proposal_status", filters.proposal_status);
      }
      if (filters.start_date) {
        params.append("start_date", filters.start_date);
      }
      if (filters.end_date) {
        params.append("end_date", filters.end_date);
      }
      
      // Add archived filter (Admin/Sales Director only)
      if (filters.archived === "true") {
        params.append("archived", "true");
      }

      // Server-side pagination (API only paginates when ?page= is sent)
      params.append("page", String(page + 1));
      params.append("page_size", String(rowsPerPage));

      const queryString = params.toString();
      const url = `/leads/${queryString ? `?${queryString}` : ""}`;
      const res = await API.get(url);

      const leadsData = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setLeads(leadsData);
      const count = res.pagination?.count;
      setTotalCount(
        typeof count === "number" ? count : leadsData.length
      );
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to load leads");
      setLeads([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchText, filters, refine, page, rowsPerPage]);

  useEffect(() => {
    const queryChanged = prevQuerySigRef.current !== querySignature;
    if (prevQuerySigRef.current === null) {
      prevQuerySigRef.current = querySignature;
    } else if (queryChanged) {
      prevQuerySigRef.current = querySignature;
      setSelectedLeadIds([]);
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    const delay = queryChanged ? 300 : 0;
    const timer = setTimeout(() => {
      fetchLeads();
    }, delay);
    return () => clearTimeout(timer);
  }, [querySignature, page, rowsPerPage, fetchLeads]);

  const handleRowClick = (lead) => {
    if (!lead?.id) return;
    navigateToLeadDetail(navigate, lead.id, {
      pathname: "/leads/search",
      search: location.search,
      label: "Search Leads",
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (role === "Sales Rep" && field === "status") {
        if (value === "Closed Won" && user?.id != null) {
          next.assigned_rep = user.id;
        } else if (prev.status === "Closed Won") {
          next.assigned_rep = "all";
        }
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilters(defaultFilters);
    setRefine({ ...defaultRefine });
    setPage(0);
    try {
      sessionStorage.removeItem(SEARCH_LEADS_STATE_KEY);
    } catch {
      // ignore
    }
  };

  const handleRefineChange = (field, value) => {
    setRefine((prev) => ({ ...prev, [field]: value }));
  };

  const handleArchiveOldLeads = async () => {
    if (isReadOnly) return;
    
    setArchiving(true);
    try {
      const res = await API.post("/leads/archive-old-leads/");
      const data = res.data;
      
      toast.success(
        `✅ ${data.message || 'Archive process completed successfully'}`,
        { autoClose: 5000 }
      );
      
      // Refresh the leads list
      await fetchLeads();
      setConfirmArchive(false);
    } catch (err) {
      console.error("Archive error:", err);
      const errorMsg = err.response?.data?.detail || "Failed to archive old leads";
      toast.error(errorMsg);
    } finally {
      setArchiving(false);
    }
  };

  const visibleLeadIds = useMemo(
    () => leads.map((lead) => lead.id).filter(Boolean),
    [leads]
  );

  const allVisibleSelected =
    canDeleteLeads &&
    visibleLeadIds.length > 0 &&
    visibleLeadIds.every((id) => selectedLeadIds.includes(id));

  const someVisibleSelected =
    canDeleteLeads &&
    visibleLeadIds.some((id) => selectedLeadIds.includes(id));

  const handleToggleLeadSelection = (leadId, event) => {
    event.stopPropagation();
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleToggleSelectAllVisible = (event) => {
    event.stopPropagation();
    if (allVisibleSelected) {
      setSelectedLeadIds((prev) =>
        prev.filter((id) => !visibleLeadIds.includes(id))
      );
    } else {
      setSelectedLeadIds((prev) =>
        Array.from(new Set([...prev, ...visibleLeadIds]))
      );
    }
  };

  const handleBulkDeleteLeads = async () => {
    if (!canDeleteLeads || selectedLeadIds.length === 0) return;

    setBulkDeleting(true);
    try {
      const res = await API.post("/leads/bulk-delete/", { ids: selectedLeadIds });
      const data = res.data;
      toast.success(data.message || "Lead(s) archived successfully");
      setSelectedLeadIds([]);
      setConfirmBulkDelete(false);
      await fetchLeads();
    } catch (err) {
      console.error("Bulk delete error:", err);
      const errorMsg =
        err.response?.data?.detail || "Failed to archive selected leads";
      toast.error(errorMsg);
    } finally {
      setBulkDeleting(false);
    }
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== "all") count++;
    if (filters.category && filters.category !== "all") count++;
    if (filters.territory !== "all") count++;
    if (filters.assigned_rep !== "all") count++;
    if (filters.property_type !== "all") count++;
    if (filters.proposal_status !== "all") count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.archived === "true") count++; // Count archived filter
    return count;
  }, [filters]);

  const activeRefineCount = useMemo(() => countFilledRefine(refine), [refine]);
  const refineActive = activeRefineCount > 0;

  // Get unique property types from leads
  const propertyTypes = useMemo(() => {
    const types = leads
      .map((lead) => lead.property_type)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort();
    return types;
  }, [leads]);

  const totalPages = useMemo(() => {
    if (totalCount === 0) return 1;
    return Math.max(1, Math.ceil(totalCount / rowsPerPage));
  }, [totalCount, rowsPerPage]);

  const rangeStart = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, totalCount);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  useEffect(() => {
    setSelectedLeadIds([]);
  }, [page, rowsPerPage]);

  // If result set shrinks, stay on a valid page (e.g. restored session page too high)
  useEffect(() => {
    if (totalCount === 0) return;
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalCount, rowsPerPage, page]);

  // Helper to format full name
  const getFullName = (lead) => {
    const first = lead?.first_name || "";
    const last = lead?.last_name || "";
    return `${first} ${last}`.trim() || "—";
  };

  // Helper to format full address
  const getFullAddress = (lead) => {
    const parts = [
      lead?.address,
      lead?.city,
      formatStateAbbr(lead?.state),
      lead?.zip_code,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  return (
    <Box className="customers-page search-leads-page">
      <Box className="customers-page__container">
        <header className="customers-header">
          <div>
            <Typography component="h1" className="customers-header__title">
              Search Leads
            </Typography>
            <Typography component="p" className="customers-header__subtitle">
              Search and filter leads across all territories. Click a lead to view details.
            </Typography>
          </div>
          {can.createLead && (
            <Button
              variant="contained"
              disableElevation
              className="customers-add-button"
              startIcon={<Plus size={20} />}
              onClick={() => setAddLeadOpen(true)}
            >
              Add a Lead
            </Button>
          )}
        </header>

        <section className="customers-toolbar" aria-label="Lead search">
          <TextField
            data-tour="search-bar"
            className="customers-search"
            placeholder="Search by address or customer name"
            helperText={
              refineActive
                ? "Simple search is paused while Refine search is in use"
                : ""
            }
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={refineActive}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} strokeWidth={2} />
                </InputAdornment>
              ),
              endAdornment: searchText && !refineActive && (
                <Button
                  size="small"
                  onClick={() => setSearchText("")}
                  sx={{ minWidth: "auto", p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              ),
            }}
          />

          <Button
            data-tour="filter-toggle"
            type="button"
            variant="outlined"
            className={`customers-advanced-button${showFilters ? " is-active" : ""}`}
            startIcon={<FilterListIcon />}
            onClick={() => {
              setShowFilters((open) => !open);
              setShowRefine(false);
            }}
          >
            {showFilters ? "Hide Filters" : "Filters"}
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  minWidth: 20,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              />
            )}
          </Button>
          <Button
            data-tour="refine-toggle"
            type="button"
            variant="outlined"
            className={`customers-advanced-button${showRefine ? " is-active" : ""}`}
            startIcon={<TuneIcon />}
            onClick={() => {
              setShowRefine((open) => !open);
              setShowFilters(false);
            }}
          >
            {showRefine ? "Hide Refine" : "Refine Search"}
            {activeRefineCount > 0 && (
              <Chip
                label={activeRefineCount}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  minWidth: 20,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              />
            )}
          </Button>

          {(role === "Admin" || role === "Sales Director") && (
            <DisabledButtonWrapper
              isDisabled={isReadOnly || archiving}
              tooltipText={archiving ? "Archiving in progress..." : "Read-only mode: This action is disabled"}
            >
              <Button
                variant="outlined"
                color="warning"
                className="search-leads-archive-button"
                startIcon={<ArchiveIcon />}
                onClick={() => {
                  if (isReadOnly || archiving) return;
                  setConfirmArchive(true);
                }}
                disabled={isReadOnly || archiving}
              >
                {archiving ? "Archiving..." : "Archive Old Leads"}
              </Button>
            </DisabledButtonWrapper>
          )}
          {canDeleteLeads && selectedLeadIds.length > 0 && (
            <DisabledButtonWrapper
              isDisabled={bulkDeleting}
              tooltipText={bulkDeleting ? "Archiving selected leads..." : ""}
            >
              <Button
                variant="outlined"
                color="error"
                className="search-leads-delete-button"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  if (bulkDeleting) return;
                  setConfirmBulkDelete(true);
                }}
                disabled={bulkDeleting}
              >
                {bulkDeleting
                  ? "Archiving..."
                  : `Delete Selected (${selectedLeadIds.length})`}
              </Button>
            </DisabledButtonWrapper>
          )}
          {(activeFilterCount > 0 || activeRefineCount > 0 || Boolean(searchText.trim())) && (
            <Button
              type="button"
              className="customers-clear-button"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear all
            </Button>
          )}
          <Typography className="customers-result-count">
            {totalCount.toLocaleString()} lead{totalCount === 1 ? "" : "s"}
          </Typography>
        </section>

        {showFilters && (
          <section className="customers-advanced-panel search-leads-panel" aria-label="Lead filters">
            <div className="search-leads-filter-grid">
              <LeadFilterRow label="Status">
                <select
                  className="customers-filter-input"
                  value={filters.status}
                  onChange={(event) => handleFilterChange("status", event.target.value)}
                >
                  <option value="all">All Statuses</option>
                  {LEAD_STATUS_DEFINITIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.value}
                    </option>
                  ))}
                </select>
              </LeadFilterRow>

              {canViewCategory && (
                <LeadFilterRow label="Lead Source">
                  <select
                    className="customers-filter-input"
                    value={filters.category || "all"}
                    onChange={(event) => handleFilterChange("category", event.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {leadCategories.map((cat) => (
                      <option key={cat.id} value={String(cat.id)}>
                        {displayLeadCategoryLabel(cat.label)}
                      </option>
                    ))}
                  </select>
                </LeadFilterRow>
              )}

              <LeadFilterRow label="Territory">
                <select
                  className="customers-filter-input"
                  value={filters.territory == null ? "all" : String(filters.territory)}
                  onChange={(event) => handleFilterChange("territory", event.target.value)}
                >
                  <option value="all">All Territories</option>
                  {territories.map((territory) => (
                    <option key={territory.id} value={String(territory.id)}>
                      {territory.name}
                    </option>
                  ))}
                </select>
              </LeadFilterRow>

              <LeadFilterRow label="Assigned Rep">
                <select
                  className="customers-filter-input"
                  value={
                    lockAssignedRepToSelf && user?.id != null
                      ? String(user.id)
                      : String(filters.assigned_rep ?? "all")
                  }
                  onChange={(event) => handleFilterChange("assigned_rep", event.target.value)}
                  disabled={lockAssignedRepToSelf}
                >
                  <option value="all">All Reps</option>
                  {users
                    .filter((rep) => rep.groups?.some((g) => typeof g === "string" ? g.includes("Rep") : g.name?.includes("Rep")))
                    .map((rep) => (
                      <option key={rep.id} value={String(rep.id)}>
                        {rep.full_name || rep.username}
                      </option>
                    ))}
                </select>
              </LeadFilterRow>

              <LeadFilterRow label="Property Type">
                <select
                  className="customers-filter-input"
                  value={filters.property_type}
                  onChange={(event) => handleFilterChange("property_type", event.target.value)}
                >
                  <option value="all">All Types</option>
                  {propertyTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </LeadFilterRow>

              <LeadFilterRow label="Proposal Status">
                <select
                  className="customers-filter-input"
                  value={filters.proposal_status}
                  onChange={(event) => handleFilterChange("proposal_status", event.target.value)}
                >
                  <option value="all">All Proposals</option>
                  <option value="draft">Draft (Not Sent)</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="none">No Proposal</option>
                </select>
              </LeadFilterRow>

              <LeadFilterRow label="Start Date">
                <input
                  className="customers-filter-input"
                  type="date"
                  value={filters.start_date}
                  onChange={(event) => handleFilterChange("start_date", event.target.value)}
                />
              </LeadFilterRow>

              <LeadFilterRow label="End Date">
                <input
                  className="customers-filter-input"
                  type="date"
                  value={filters.end_date}
                  onChange={(event) => handleFilterChange("end_date", event.target.value)}
                />
              </LeadFilterRow>

              {(role === "Admin" || role === "Sales Director") && (
                <LeadFilterRow label="Lead Status">
                  <select
                    className="customers-filter-input"
                    value={filters.archived}
                    onChange={(event) => handleFilterChange("archived", event.target.value)}
                  >
                    <option value="false">Active Leads</option>
                    <option value="true">Archived Leads</option>
                  </select>
                </LeadFilterRow>
              )}
            </div>
          </section>
        )}

        {showRefine && (
          <section className="customers-advanced-panel search-leads-panel" data-tour="refine-search">
            <div className="search-leads-filter-grid">
              <LeadFilterRow label="First name">
                <input
                  className="customers-filter-input"
                  value={refine.first_name}
                  onChange={(event) => handleRefineChange("first_name", event.target.value)}
                />
              </LeadFilterRow>
              <LeadFilterRow label="Last name">
                <input
                  className="customers-filter-input"
                  value={refine.last_name}
                  onChange={(event) => handleRefineChange("last_name", event.target.value)}
                />
              </LeadFilterRow>
              <LeadFilterRow label="Address">
                <input
                  className="customers-filter-input"
                  value={refine.address}
                  autoComplete="off"
                  name="refine-street"
                  onChange={(event) => handleRefineChange("address", event.target.value)}
                />
              </LeadFilterRow>
              <LeadFilterRow label="City">
                <input
                  className="customers-filter-input"
                  value={refine.city}
                  onChange={(event) => handleRefineChange("city", event.target.value)}
                />
              </LeadFilterRow>
              <LeadFilterRow label="State">
                <select
                  className="customers-filter-input"
                  value={normalizeStateValue(refine.state)}
                  onChange={(event) => handleRefineChange("state", event.target.value)}
                >
                  <option value="">Any state</option>
                  {US_STATES.map((state) => (
                    <option key={state.abbr} value={state.abbr}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </LeadFilterRow>
              <LeadFilterRow label="Zip code">
                <input
                  className="customers-filter-input"
                  value={refine.zip_code}
                  onChange={(event) => handleRefineChange("zip_code", event.target.value)}
                />
              </LeadFilterRow>
              <LeadFilterRow label="Phone">
                <input
                  className="customers-filter-input"
                  inputMode="tel"
                  value={refine.phone}
                  onChange={(event) => handleRefineChange("phone", event.target.value)}
                />
              </LeadFilterRow>
            </div>
          </section>
        )}

        <Card className="customers-table-card">

          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography>Loading leads...</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", overflow: "auto" }}>
                {isMobile ? (
		<>
            
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
                    {leads.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary" gutterBottom>No leads found</Typography>
                        {can.createLead && (
                        <Button variant="contained" color="primary" size="small" onClick={() => setAddLeadOpen(true)} sx={{ mt: 1 }}>
                          Add a lead
                        </Button>
                        )}
                      </Box>
                    ) : (
                      leads.map((lead) => (
                        <Card
                          key={lead.id}
                          onClick={() => handleRowClick(lead)}
                          sx={{
                            ...mobileLeadCardSx,
                            position: "relative",
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {/* Identity Row */}
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                              {/* Avatar */}
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(25,118,210,0.12)",
                                  color: "primary.main",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  flexShrink: 0
                                }}
                              >
                                {getFullName(lead)
                                  .split(" ")
                                  .map(n => n[0])
                                  .slice(0,2)
                                  .join("")}
                              </Box>

                              {/* Name + Address */}
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  sx={{ color: "primary.main", lineHeight: 1.2 }}
                                >
                                  {getFullName(lead)}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: 13 }}
                                >
                                  {getFullAddress(lead)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Stats Grid */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1.5,
                                fontSize: 13
                              }}
                            >
                              {/* Status */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Status
                                </Typography>
                                <Box>
                                  <LeadStatusCell status={lead.status} />
                                </Box>
                              </Box>

                              {/* Territory */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Territory
                                </Typography>
                                <Typography variant="body2">
                                  {lead.territory_name || "—"}
                                </Typography>
                              </Box>

                              {canViewCategory && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Lead Source
                                  </Typography>
                                  <Box sx={{ mt: 0.25 }}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={displayLeadCategoryLabel(lead.category_label)}
                                      sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Phone */}
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Phone
                                </Typography>
                                <Typography variant="body2">
                                  {formatPhoneDisplay(lead.phone) || "—"}
                                </Typography>
                              </Box>

                              {/* Assigned Rep */}
                              <Box sx={{ pr: canViewCategory ? 0 : 7 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Rep
                                </Typography>
                                <Typography variant="body2">
                                  {lead.assigned_rep_name || "—"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          
                          <IconButton
                            aria-label="View lead"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(lead);
                            }}
                            sx={{
                              position: "absolute",
                              bottom: 14,
                              right: 14,
                              width: 42,
                              height: 42,
                              borderRadius: "12px",
                              color: "primary.main",
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(0,99,242,0.18)"
                                  : "rgba(0,99,242,0.08)",
                              "&:hover": {
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(0,99,242,0.28)"
                                    : "rgba(0,99,242,0.14)",
                              },
                            }}
                          >
                            <ChevronRightIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Card>
                      ))
                    )}
                  </Box>

  {isMobile && leads.length > 0 && (
    <Box
      sx={{
        px: 2,
        py: 1.75,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        borderTop: 1,
        borderColor: "divider",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "grey.50",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ width: "100%" }}
      >
        Showing {rangeStart}-{rangeEnd} of {totalCount}{" "}
        {totalCount === 1 ? "lead" : "leads"}
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
        spacing={1}
        useFlexGap
        sx={{ width: "100%", maxWidth: 420 }}
      >
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel id="search-leads-rows-mobile-label">Rows</InputLabel>
          <Select
            labelId="search-leads-rows-mobile-label"
            label="Rows"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={page === 0}
          onClick={() => handleChangePage(null, page - 1)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 88,
            ...paginationNavButtonHoverSx,
          }}
        >
          Previous
        </Button>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            minWidth: 52,
            textAlign: "center",
            fontWeight: 600,
            px: 0.5,
          }}
        >
          {page + 1} / {totalPages}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={page >= totalPages - 1}
          onClick={() => handleChangePage(null, page + 1)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 88,
            ...paginationNavButtonHoverSx,
          }}
        >
          Next
        </Button>
      </Stack>
    </Box>
  )}

</>


                ) : (
                  // Desktop Table View
                  <>
                    <Box className="customers-table-scroll">
                      <Box component="table" className="customers-table search-leads-table" data-tour="results-table">
                        <thead>
                          <tr>
                            {canDeleteLeads && (
                              <th className="search-leads-check-col">
                                <Checkbox
                                  size="small"
                                  checked={allVisibleSelected}
                                  indeterminate={someVisibleSelected && !allVisibleSelected}
                                  onClick={handleToggleSelectAllVisible}
                                  inputProps={{ "aria-label": "Select all visible leads" }}
                                />
                              </th>
                            )}
                            <th scope="col">Name</th>
                            <th scope="col">Address</th>
                            <th scope="col">Status</th>
                            {canViewCategory && <th scope="col">Lead Source</th>}
                            <th scope="col">Territory</th>
                            <th scope="col">Assigned Rep</th>
                            <th scope="col">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leads.length === 0 ? (
                            <tr>
                              <td className="customers-table-empty" colSpan={(canViewCategory ? 7 : 6) + (canDeleteLeads ? 1 : 0)}>
                                <div className="customers-table-empty__title">No leads found</div>
                                {can.createLead && (
                                  <Button variant="contained" className="customers-add-button" onClick={() => setAddLeadOpen(true)} sx={{ mt: 1.5 }}>
                                    Add a lead
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ) : (
                            leads.map((lead) => (
                              <tr
                                key={lead.id}
                                className={selectedLeadIds.includes(lead.id) ? "is-selected" : ""}
                                onClick={() => handleRowClick(lead)}
                              >
                                {canDeleteLeads && (
                                  <td className="search-leads-check-col" onClick={(event) => event.stopPropagation()}>
                                    <Checkbox
                                      size="small"
                                      checked={selectedLeadIds.includes(lead.id)}
                                      onClick={(event) => handleToggleLeadSelection(lead.id, event)}
                                      inputProps={{ "aria-label": `Select ${getFullName(lead)}` }}
                                    />
                                  </td>
                                )}
                                <td>
                                  <span className="customer-name-link">{getFullName(lead)}</span>
                                </td>
                                <td>{getFullAddress(lead)}</td>
                                <td>
                                  <LeadStatusCell status={lead.status} />
                                </td>
                                {canViewCategory && (
                                  <td>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={displayLeadCategoryLabel(lead.category_label)}
                                      sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))}
                                    />
                                  </td>
                                )}
                                <td className={!lead.territory_name ? "customer-table-muted" : ""}>{lead.territory_name || "—"}</td>
                                <td className={!lead.assigned_rep_name ? "customer-table-muted" : ""}>{lead.assigned_rep_name || "—"}</td>
                                <td>
                                  {lead.created_at
                                    ? new Date(lead.created_at).toLocaleDateString()
                                    : "—"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Box>
                    </Box>
                    <footer className="customers-table-footer">
                      <Typography className="customers-table-count">
                        Showing {rangeStart}-{rangeEnd} of {totalCount}{" "}
                        {totalCount === 1 ? "lead" : "leads"}
                      </Typography>
                      <Stack
                        className="search-leads-pager"
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        spacing={1}
                        useFlexGap
                      >
                        <FormControl size="small" variant="outlined" sx={{ minWidth: 108 }}>
                          <InputLabel id="search-leads-rows-label">Rows</InputLabel>
                          <Select
                            labelId="search-leads-rows-label"
                            label="Rows"
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                          >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => (
                              <MenuItem key={n} value={n}>
                                {n}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          className="customers-page-button"
                          disabled={page === 0}
                          onClick={() => handleChangePage(null, page - 1)}
                        >
                          Previous
                        </Button>
                        <Pagination
                          count={totalPages}
                          page={page + 1}
                          onChange={(_, value) => handleChangePage(null, value - 1)}
                          size="small"
                          siblingCount={1}
                          boundaryCount={1}
                          hidePrevButton
                          hideNextButton
                        />
                        <Button
                          className="customers-page-button"
                          disabled={page >= totalPages - 1}
                          onClick={() => handleChangePage(null, page + 1)}
                        >
                          Next
                        </Button>
                      </Stack>
                    </footer>
                  </>
                )}
              </Box>
            </>
          )}
        </Card>

      {/* Archive Confirmation Dialog */}
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={handleArchiveOldLeads}
        title="Archive Old Leads"
        message="This will archive all leads older than 45 days without a status change. This action cannot be undone. Continue?"
        confirmText="Yes, Archive"
        cancelText="Cancel"
        severity="warning"
      />
      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={handleBulkDeleteLeads}
        title="Delete Selected Leads"
        message={`Archive ${selectedLeadIds.length} selected lead(s)? They will be removed from active lists and can be restored later.`}
        confirmText={bulkDeleting ? "Archiving..." : "Yes, Delete"}
        cancelText="Cancel"
        severity="error"
      />
      <AddSearchLeadDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        role={role}
        territories={territories}
        users={users}
        onCreated={(lead) => {
          if (!lead?.id) return;
          navigateToLeadDetail(navigate, lead.id, {
            pathname: "/leads/search",
            search: location.search,
            label: "Search Leads",
          });
        }}
      />
      </Box>
    </Box>
  );
}